import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

/**
 * Customer-facing design helper chat widget.
 * Renders a floating action button; expands into a chat panel.
 * `productContext` (optional) tells the AI what product the shopper is on.
 */
export default function DesignHelperWidget({ productContext = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/chat/design-help", {
        session_id: sessionId,
        messages: next,
        product_context: productContext,
      });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "Sorry — I had a hiccup. Please try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => {
            setOpen(true);
            if (messages.length === 0) {
              const opener = productContext?.name
                ? `Hi! I'm here to help you customize the ${productContext.name}. What size, color, or personalization did you have in mind?`
                : "Hi! I'm the Hampton Blu design helper. Tell me a bit about what you're looking for — apparel type, occasion, colors — and I'll help narrow it down.";
              setMessages([{ role: "assistant", content: opener }]);
            }
          }}
          className="fixed bottom-6 right-6 z-40 bg-[color:var(--hb-navy)] text-white rounded-full p-4 shadow-lg hover:scale-105 transition"
          data-testid="design-helper-open"
          aria-label="Open design helper"
        >
          <MessageCircle size={22}/>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-[380px] h-[560px] bg-white border border-neutral-200 shadow-2xl flex flex-col" data-testid="design-helper-panel">
          <div className="flex items-center justify-between p-3 bg-[color:var(--hb-navy)] text-white">
            <div className="flex items-center gap-2">
              <MessageCircle size={16}/>
              <span className="font-medium text-sm">Hampton Blu Design Helper</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="design-helper-close" aria-label="Close"><X size={16}/></button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="design-helper-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`hm-${m.role}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[color:var(--hb-navy)] text-white" : "bg-neutral-100 text-neutral-900"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-neutral-100 px-3 py-2 text-xs flex items-center gap-1.5"><Loader2 size={10} className="animate-spin"/> Thinking…</div></div>
            )}
            <div ref={endRef}/>
          </div>

          <div className="border-t border-neutral-200 p-2 flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") send(); }}
              placeholder="Type your question…"
              className="flex-1 border border-neutral-300 px-3 py-1.5 text-sm"
              data-testid="design-helper-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="bg-[color:var(--hb-navy)] text-white px-3 disabled:opacity-40"
              data-testid="design-helper-send"
            >
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
